import { useState } from 'react';

interface Feriado {
  id: string;
  fecha: string;
  nombre: string;
  tipo: string;
}

interface Props {
  onSave: (feriado: Omit<Feriado, 'id'>) => void;
  onClose: () => void;
}

const tiposFeriado = [
  'Feriado Nacional',
  'Cierre Especial',
  'Mantenimiento',
  'Evento Privado',
];

export default function AgregarFeriadoModal({ onSave, onClose }: Props) {
  const [fecha, setFecha] = useState('');
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState('Feriado Nacional');
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState<{ fecha?: string; nombre?: string }>({});

  const validateForm = () => {
    const newErrors: { fecha?: string; nombre?: string } = {};

    if (!fecha) {
      newErrors.fecha = 'La fecha es requerida';
    }

    if (!nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    } else if (nombre.trim().length < 3) {
      newErrors.nombre = 'El nombre debe tener al menos 3 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    onSave({
      fecha,
      nombre: nombre.trim(),
      tipo,
    });

    setSaved(true);
    setTimeout(() => {
      onClose();
    }, 800);
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose}></div>

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-auto">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Agregar Feriado</h3>
                <p className="text-sm text-gray-500 mt-1">Registra un nuevo feriado o cierre especial</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>
          </div>

          <div className="px-6 py-5 space-y-5">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-4">Agregar un feriado individual:</p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={fecha}
                  min={today}
                  onChange={(e) => {
                    setFecha(e.target.value);
                    if (errors.fecha) setErrors((prev) => ({ ...prev, fecha: undefined }));
                  }}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm ${
                    errors.fecha ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.fecha && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.fecha}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Feriado <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => {
                    setNombre(e.target.value);
                    if (errors.nombre) setErrors((prev) => ({ ...prev, nombre: undefined }));
                  }}
                  placeholder="Ej: Día del Trabajo"
                  maxLength={100}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm ${
                    errors.nombre ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.nombre && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.nombre}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Cierre</label>
                <div className="grid grid-cols-2 gap-2">
                  {tiposFeriado.map((t) => (
                    <button
                      key={t}
                      onClick={() => setTipo(t)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        tipo === t ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {fecha && nombre && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Vista previa:</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-800">{nombre}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(`${fecha}T12:00:00`).toLocaleDateString('es-CL', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        tipo === 'Feriado Nacional'
                          ? 'bg-red-100 text-red-700'
                          : tipo === 'Cierre Especial'
                            ? 'bg-orange-100 text-orange-700'
                            : tipo === 'Mantenimiento'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-purple-100 text-purple-700'
                      }`}
                    >
                      {tipo}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saved}
              className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                saved ? 'bg-green-500' : 'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              {saved ? (
                <>
                  <i className="ri-check-line"></i>
                  Guardado
                </>
              ) : (
                <>
                  <i className="ri-add-line"></i>
                  Agregar Feriado
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
